import React, { useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useMutation, useQueryClient } from 'react-query'
import PropTypes from 'prop-types'
import { useDrag, useDrop } from 'react-dnd'
import styled from 'styled-components'
import withApiData from '../../HOC/withApiData'
import { deleteExistingResource, reorderResources } from './API'
import { showModal } from '../../../actions/modal'
import { ItemTypes } from '../Journey/ItemTypes'
import _SortOrderIcon from '../../../static/images/mycourses/SortOrder.svg'
import FileTypeIcon from '../../mini/FileTypeIcon'
import FlexAlignCenter from '../../presentational/FlexAlignCenter'
import colors from '../../../configuration/colors'
import _Edit from '../../../static/images/edit.svg'
import _Delete from '../../../static/images/icon_delete.svg'
import MarkdownLabel from '../../mini/MarkdownLabel'

const ResourceContainer = styled.div`
  margin: 11px 0;
  background: none;
  border: 1px solid #dedfe0;
  border-radius: 4px;
  background-color: #ffffff;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-content: center;
  align-items: center;
`

const MentorFileInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 22px;
  flex: 90%;
  align-content: center;
  align-items: center;
`

const SortIcon = styled.img.attrs({
  src: _SortOrderIcon
})`
  width: 19px;
  height: 9px;
  margin-right: 18px;
`

const FileName = styled.a`
  color: ${colors.charcoal4};
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 21px;
`

const FileDescription = styled.div`
  color: ${colors.black};
  font-size: 14px;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 18px;
  margin: 0 0 10px 100px;
`

const EditIcon = styled.div`
  background: url(${_Edit}) no-repeat;
  background-size: 100%;
  height: 14px;
  width: 14px;
  cursor: pointer;
`

const DeleteIcon = styled.div`
  background: url(${_Delete}) no-repeat;
  background-size: 100%;
  height: 17px;
  width: 14px;
  cursor: pointer;
`

const ResourceRow = ({
  apiData,
  activityQuestId,
  levelId,
  resource,
  index,
  moveCard,
  id
}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const ref = useRef(null)
  const [, drop] = useDrop({
    accept: ItemTypes.RESOURCE_ROW,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) {
        return
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect()

      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      const clientOffset = monitor.getClientOffset()

      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      moveCard(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
    drop(item) {
      const index = item.index - 1 === -1 ? 0 : item.index
      reorderResources(apiData, levelId, activityQuestId, id, index)
    }
  })

  const [, drag] = useDrag({
    type: ItemTypes.RESOURCE_ROW,
    item: () => {
      return { id, index }
    },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  })

  drag(drop(ref))

  const deleteResource = useMutation(
    ({ apiData, levelId, activityQuestId, resourceId }) =>
      deleteExistingResource(apiData, levelId, activityQuestId, resourceId)
  )

  const handleDeleteResource = () => {
    deleteResource.mutate(
      {
        apiData,
        levelId,
        activityQuestId,
        resourceId: resource.id
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('resources-' + activityQuestId)
        }
      }
    )
  }

  return (
    <div ref={ref} style={{ boxShadow: 'none' }}>
      <ResourceContainer>
        <FlexContainer>
          <MentorFileInfoContainer>
            <FlexAlignCenter>
              <SortIcon />
              <FileTypeIcon type={resource.resource_type} />
              <FileName
                style={{ color: colors.charcoal4 }}
                href={
                  resource.file_url === null ? resource.url : resource.file_url
                }
                target="_blank"
              >
                {resource.name}
              </FileName>
              <EditIcon
                style={{ marginLeft: '15px' }}
                onClick={() =>
                  dispatch(
                    showModal('EDIT_RESOURCE_MODAL', {
                      levelId,
                      activityQuestId,
                      resourceId: resource.id
                    })
                  )
                }
              />
            </FlexAlignCenter>
            <DeleteIcon
              onClick={() =>
                dispatch(
                  showModal('DELETE_RESOURCE_MODAL', {
                    levelId,
                    activityQuestId,
                    resourceId: resource.id,
                    handleDeleteResource
                  })
                )
              }
            />
          </MentorFileInfoContainer>
        </FlexContainer>
        {resource.description !== null && (
          <FileDescription>
            <MarkdownLabel value={resource.description} />
          </FileDescription>
        )}
      </ResourceContainer>
    </div>
  )
}

ResourceRow.propTypes = {
  apiData: PropTypes.object.isRequired,
  activityCourse: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  resource: PropTypes.object.isRequired,
  selectedAcademicYearId: PropTypes.number.isRequired,
  moveCard: PropTypes.func.isRequired
}

export default withApiData(ResourceRow)



// WEBPACK FOOTER //
// ./src/components/mycourses/Activities/ResourceRow.js